import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Youtube, Instagram, Mic, Play } from 'lucide-react';
import { useState, useEffect } from 'react';

const AnimatedCounter = ({ target, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView({ triggerOnce: true });

  useEffect(() => {
    if (!inView) return;
    const targetNum = parseInt(target.replace(/[^0-9]/g, ''));
    const duration = 2000;
    const steps = 60;
    const increment = targetNum / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= targetNum) {
        setCount(targetNum);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [inView, target]);

  return <span ref={ref}>{count}{suffix}</span>;
};

export const ContentCreatorSection = ({ data }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="content" className="py-24 md:py-32 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            {data.title}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {data.description}
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
          {data.stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="text-center">
              <div className="text-5xl font-bold text-amber-600 dark:text-amber-400 mb-2">
                <AnimatedCounter target={stat.number.replace(/[^0-9]/g, '')} suffix={stat.number.replace(/[0-9]/g, '')} />
              </div>
              <p className="text-gray-600 dark:text-gray-400">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Topics */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          {data.topics.map((topic, index) => (
            <motion.span
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.5, delay: 0.8 + index * 0.05 }}
              className="px-4 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-full text-sm font-medium">
              {topic}
            </motion.span>
          ))}
        </div>

        {/* Platform Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { name: 'YouTube', icon: Youtube, href: data.platforms.youtube, color: 'from-red-500 to-pink-500' },
            { name: 'Instagram', icon: Instagram, href: data.platforms.instagram, color: 'from-purple-500 to-pink-500' },
            { name: 'Happy Lab', icon: Instagram, href: 'https://www.instagram.com/happylab.app/', color: 'from-amber-500 to-rose-500' },
            { name: 'Podcast', icon: Play, href: data.platforms.podcast, color: 'from-orange-500 to-red-500' },
          ].map((platform, index) => (
            <motion.a
              key={index}
              href={platform.href}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 1.2 + index * 0.1 }}
              whileHover={{ scale: 1.05, y: -4 }}
              className="group bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 text-center">
              <div className={`w-14 h-14 bg-gradient-to-br ${platform.color} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                <platform.icon className="w-7 h-7 text-white" />
              </div>
              <p className="font-semibold text-gray-900 dark:text-white">{platform.name}</p>
            </motion.a>
          ))}
        </div>

        {/* Video Embeds */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8, delay: 1.6 }}
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Cityology Video */}
          <div>
            <div className="aspect-video bg-gray-900 rounded-3xl overflow-hidden shadow-2xl">
              <video 
                controls 
                className="w-full h-full"
                poster="/images/counseling-session.png"
              >
                <source src="/videos/cityology-sample.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="text-center mt-4 text-gray-600 dark:text-gray-400 font-medium">
              Cityology: Exploring cities, history and urban planning
            </p>
          </div>

          {/* Trading Psychology Video */}
          <div>
            <div className="aspect-video bg-gray-900 rounded-3xl overflow-hidden shadow-2xl">
              <video 
                controls 
                className="w-full h-full"
              >
                <source src="/videos/trading-psychology.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
            <p className="text-center mt-4 text-gray-600 dark:text-gray-400 font-medium">
              Trading Psychology: Understanding emotions and decision-making
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};